import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import type { ResponsePromptVariant } from '@innermate/shared';
import { Pencil, Plus, Trash2 } from 'lucide-react';

interface Props {
  variants: ResponsePromptVariant[];
  activeVariantId: string | null;
  /** 탭 클릭 시. dirty 상태에서의 confirm 처리는 부모가 책임. */
  onSelect: (variantId: string) => void;
  onAdd: () => void;
  onRename: (variantId: string) => void;
  onDelete: (variantId: string) => void;
}

/**
 * 모델별 variant 탭 strip. 좌측 끝에 "기본" 고정, 그 뒤로 model_match 알파벳 순,
 * 마지막에 "+ 모델 추가". 활성 탭은 outline, hover 시 우측에 작은 액션 아이콘.
 *
 * 디자인 메모: 일반 탭과 달리 hover 시 우측에 inline 액션 (이름변경/삭제) 노출.
 * "기본" 탭은 삭제 비활성 (versions 0 일 때만 이름변경도 의미 없음 — temperature 만 편집 가능).
 */
export function VariantTabs({
  variants,
  activeVariantId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
}: Props) {
  // 기본 → 그 외 (sortOrder, modelMatch 순)
  const sorted = [...variants].sort((a, b) => {
    if (a.modelMatch === '' && b.modelMatch !== '') return -1;
    if (a.modelMatch !== '' && b.modelMatch === '') return 1;
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.modelMatch.localeCompare(b.modelMatch);
  });

  return (
    <div className="flex items-center gap-1 overflow-x-auto border-b border-border bg-surface-muted/30 px-3 py-1.5">
      {sorted.map((v) => {
        const isActive = v.id === activeVariantId;
        const isDefault = v.modelMatch === '';
        const display = v.label?.trim() || (isDefault ? '기본' : v.modelMatch);
        return (
          <div
            key={v.id}
            className={cn(
              'group inline-flex items-center rounded-md border text-xs transition-colors',
              isActive
                ? 'border-accent bg-accent/10 text-accent'
                : 'border-transparent hover:bg-surface',
            )}
          >
            <button
              type="button"
              onClick={() => onSelect(v.id)}
              className="px-2 py-1 cursor-pointer"
              title={isDefault ? '기본 variant (매칭 실패 시 fallback)' : `model: ${v.modelMatch}`}
            >
              <span className="font-medium">{display}</span>
              {v.currentVersion == null ? (
                <span className="ml-1 text-foreground-muted">· 비어있음</span>
              ) : null}
            </button>
            <div
              className={cn(
                'flex items-center pr-1 gap-0.5',
                'opacity-0 group-hover:opacity-100 transition-opacity',
                isActive && 'opacity-100',
              )}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRename(v.id);
                }}
                className="p-0.5 rounded hover:bg-foreground/10 cursor-pointer"
                title="이름·기본 temperature 수정"
                aria-label="variant 수정"
              >
                <Pencil className="size-3" />
              </button>
              {!isDefault ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(v.id);
                  }}
                  className="p-0.5 rounded hover:bg-destructive/10 text-foreground-muted hover:text-destructive cursor-pointer"
                  title="variant 삭제 (versions 도 같이 삭제됨)"
                  aria-label="variant 삭제"
                >
                  <Trash2 className="size-3" />
                </button>
              ) : null}
            </div>
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onAdd}
        className="ml-1 h-7 px-2 text-xs"
      >
        <Plus className="size-3 mr-1" />
        모델 추가
      </Button>
    </div>
  );
}
